import {
    Component, Mixins,
} from 'vue-property-decorator';
import Classes from '@components/shared/mixins/classes';
import { YIcon, YLoading } from '@components/index';
import config from '@components/shared/config';
import Props from './props';

/**
 * @name Modal
 */
@Component({
    components: {
        YIcon,
        YLoading,
    },
})
class YModal extends Mixins(Props, Classes) {
    /**
     * Controls modal display
     */
    display = !!this.show;

    /**
     * Class of next button
     */
    nextClass: Array<string> = []

    /**
     * Class of back button
     */
    backClass: Array<string> = []

    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['size', 'icon', 'sharp', 'flat', 'raised', 'pill'];

    /**
     * Create Common Props for the modal.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> { // eslint-disable-line @typescript-eslint/no-explicit-any
        const commonProps: Record<string, any> = { // eslint-disable-line @typescript-eslint/no-explicit-any
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            commonProps[item] = this[item];
        });
        return commonProps;
    }

    /**
     * Create classes array for the modal.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('Modal', this.commonProps, this.baseClass || config.prefix.modal));
        if (this.fullscreen) {
            cls.push('is-size--xlarge');
        }
        if (this.forced) {
            cls.push('is-forced');
        }
        if (this.noBorder) {
            cls.push('is-no-border');
        }
        if (this.fullHeight) {
            cls.push('is-full-height');
        }
        if (this.align) {
            cls.push(`is-button-align--${this.align}`);
        }
        if (this.loading || (this.loadingOn && this.$wait.is(this.loadingOn))) {
            cls.push('is-loading');
        }
        cls.push(`is-direction--${document.dir}`);
        return cls;
    }

    /**
     * Return the class of the modal from config.
     * 
     * @returns {string}
     */
    get modalClass(): string {
        return config.prefix.modal;
    }

    /**
     * Return the status of the loading.
     * 
     * @returns {boolean}
     */
    get loadingStatus(): boolean {
        let status = false;
        if (this.loading) {
            status = true;
        }
        if (this.loadingOn && this.$wait.is(this.loadingOn)) {
            status = true;
        }
        return status;
    }

    /**
     * Return the type of the loading from config.
     * 
     * @returns {string}
     */
    get loadingType(): string {
        return config.loading.type;
    }

    /**
     * Return background color of next button.
     * 
     * @returns {*}
     */
    get getNextColor(): unknown {
        if (this.nextColor === 'transparent') {
            this.nextClass.push(`${this.modalClass}__next-button--transparent`);
            return null;
        }
        return this.nextColor;
    }

    /**
     * Return background color of back button.
     * 
     * @returns {*}
     */
    get getBackColor(): unknown {
        if (this.backColor === 'transparent') {
            this.backClass.push(`${this.modalClass}__back-button--transparent`);
            return null;
        }
        return this.backColor;
    }

    /**
     * @inheritDoc
     */
    mounted(): void {
        this.setListenerForEsc(); // Set event listener for Esc
        if (this.show) {
            document.body.style.overflow = 'hidden';
        }
    }

    /**
     * Open modal and emit open modal.
     */
    open() : void {
        this.display = true;
        this.$emit('open');
        document.body.style.overflow = 'hidden';
    }

    /**
     * Close modal and emit close event.
     */
    close() : void {
        this.display = false;
        this.$emit('close');
        document.body.style.overflow = '';
    }

    /**
     * Toggle modal view.
     */
    toggle() : void {
        if (this.display) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Set Event listener on document to close modal on Esc pressed.
     */
    setListenerForEsc(): void {
        const vm = this; // eslint-disable-line @typescript-eslint/no-this-alias
        document.addEventListener('keyup', (e) => {
            if (e.code === 'Escape' && !this.forced) {
                vm.close();
            }
        });
    }

    /**
     * Does Absolutely Nothing.
     *
     * @returns {null}
     */
    doNothing() : null {
        return null;
    }

    /**
     * Set back button click handler.
     */
    setBackClick(): void {
        if (this.backClick) {
            this.backClick();
        }
        this.$emit('back');
    }

    /**
     * Set next button click handler.
     */
    setNextClick(): void {
        if (this.backClick) {
            this.nextClick();
        }
        this.$emit('next');
    }
}

export default YModal;
